import { Observable, of } from 'rxjs';
import { UntypedFormGroup, UntypedFormArray } from '@angular/forms';
import { Directive, OnDestroy } from '@angular/core';
import { tap, take, switchMap } from 'rxjs';

@Directive()
export abstract class ConfigSection<T> implements OnDestroy {

  protected form: UntypedFormGroup | UntypedFormArray;
  private config: T;
  private isLoading = true;
  private isAlive = true;

  /**
   * Derived types must implement this method to get the initial/current values of the relevant configuration settings.
   *
   * Typically, this is done by executing logic from a back-end service.
   *
   * @protected
   */
  protected abstract initialiseConfig(): Observable<T>;

  /**
   * Derived types must implement this method to initialise their own state using the specified configuration object.
   *
   * @param config
   * @protected
   */
  protected abstract createForm(config: T): void;

  /**
   * Derived types must implement this method to save the settings within their current state.
   *
   * Typically, this is done by sending the modified data to a back-end service.
   */
  abstract submit(): Observable<boolean>;

  load(): Observable<void> {
    this.isLoading = true;
    return this.initialiseConfig().pipe(
      take(1),
      tap((config: T) => {
        this.config = config;
        this.createForm(config);
        this.isLoading = false;
      }),
      switchMap(() => of(void 0))
    );
  }

  protected get originalConfig() {
    return this.config;
  }

  get loading() {
    return this.isLoading;
  }

  get modified(): boolean {
    return this.form && !this.form.pristine;
  }

  get valid(): boolean {
    return this.form && this.form.valid === true;
  }

  get alive(): boolean {
    return this.isAlive;
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
